import { useEffect, useRef } from "react";
import { DOT_PITCH } from "./svg/DotEffect";

// Page-wide companion to the pin-art hover on the ASCII art: the site's
// background dot grid lights up around the cursor, the nearest dots rising
// toward the viewer, then fades back once the cursor moves on. The grid is
// anchored to the page, so it scrolls with the content and lines up with the
// static DotEffect patches. It sits behind all page content, only draws near
// the cursor, and does nothing while the cursor is still or on touch screens.

const RADIUS = 110; // reach of the cursor, CSS px
const MAX_LIFT = 3; // how far a fully raised dot moves, CSS px
const LIFT_X = -0.35; // up and slightly left, matching AsciiArt
const LIFT_Y = -0.94;
const MAX_SIZE = 3; // size of a fully raised dot, CSS px (resting dots are 1px)
const GLOW = 16; // diameter of the halo around a fully raised dot, CSS px
const RISE = 0.25; // easing per 60fps frame toward a higher target
const SETTLE = 0.06; // easing per 60fps frame back down, leaving a short trail
const REST = 0.004; // below this a dot counts as dark
const DIM = [135, 135, 148]; // #878794, the static grid's dot color
const LIT = [110, 175, 255]; // brand blue, lightened
const KEY_COLS = 1 << 12; // dot key = row * KEY_COLS + column

function DotGridGlow() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    // Soft round halo, drawn scaled under each lit dot.
    const glow = document.createElement("canvas");
    glow.width = glow.height = 64;
    const glowCtx = glow.getContext("2d")!;
    const gradient = glowCtx.createRadialGradient(32, 32, 0, 32, 32, 32);
    gradient.addColorStop(0, `rgba(${LIT.join(", ")}, 0.35)`);
    gradient.addColorStop(1, `rgba(${LIT.join(", ")}, 0)`);
    glowCtx.fillStyle = gradient;
    glowCtx.fillRect(0, 0, 64, 64);

    const heights = new Map<number, number>();
    let pointer: { x: number; y: number } | null = null; // viewport px
    let dpr = 1;
    let frameId = 0;
    let last = 0;

    // Sized to the viewport excluding scrollbars, so canvas and CSS pixels match.
    const fit = () => {
      const { clientWidth, clientHeight } = document.documentElement;
      dpr = Math.min(2, window.devicePixelRatio || 1);
      canvas.width = Math.round(clientWidth * dpr);
      canvas.height = Math.round(clientHeight * dpr);
      canvas.style.width = `${clientWidth}px`;
      canvas.style.height = `${clientHeight}px`;
    };

    const frame = (now: number) => {
      const dt = last ? Math.min(4, (now - last) / (1000 / 60)) : 1;
      last = now;
      const kRise = 1 - Math.pow(1 - RISE, dt);
      const kSettle = 1 - Math.pow(1 - SETTLE, dt);
      const still = reducedMotion.matches;
      const sx = window.scrollX;
      const sy = window.scrollY;

      // Target heights for the dots near the cursor, in page coordinates.
      const targets = new Map<number, number>();
      if (pointer) {
        const px = pointer.x + sx;
        const py = pointer.y + sy;
        const c0 = Math.max(0, Math.ceil((px - RADIUS) / DOT_PITCH));
        const c1 = Math.floor((px + RADIUS) / DOT_PITCH);
        const r0 = Math.max(0, Math.ceil((py - RADIUS) / DOT_PITCH));
        const r1 = Math.floor((py + RADIUS) / DOT_PITCH);
        for (let r = r0; r <= r1; r++) {
          for (let c = c0; c <= c1; c++) {
            const d = Math.hypot(c * DOT_PITCH + 0.5 - px, r * DOT_PITCH + 0.5 - py) / RADIUS;
            if (d >= 1) continue;
            const key = r * KEY_COLS + c;
            targets.set(key, (1 - d * d) ** 2);
            if (!heights.has(key)) heights.set(key, 0);
          }
        }
      }

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      let moving = false;
      for (const [key, cur] of heights) {
        const target = targets.get(key) ?? 0;
        const h = cur + (target - cur) * (target > cur ? kRise : kSettle);
        if (h < REST && target === 0) {
          heights.delete(key);
          continue;
        }
        heights.set(key, h);
        if (Math.abs(target - h) > REST) moving = true;

        const c = key % KEY_COLS;
        const r = (key - c) / KEY_COLS;
        const lift = still ? 0 : MAX_LIFT * h;
        const cx = (c * DOT_PITCH + 0.5 - sx + LIFT_X * lift) * dpr;
        const cy = (r * DOT_PITCH + 0.5 - sy + LIFT_Y * lift) * dpr;
        const halo = GLOW * h * dpr;
        ctx.globalAlpha = h;
        ctx.drawImage(glow, cx - halo / 2, cy - halo / 2, halo, halo);
        // Whole device pixels keep the dots crisp, like the static grid.
        const size = Math.max(1, Math.round((1 + (still ? 0 : MAX_SIZE - 1) * h) * dpr));
        const [red, green, blue] = DIM.map((v, i) => Math.round(v + (LIT[i] - v) * h));
        ctx.globalAlpha = Math.min(1, h * 1.5);
        ctx.fillStyle = `rgb(${red}, ${green}, ${blue})`;
        ctx.fillRect(Math.round(cx - size / 2), Math.round(cy - size / 2), size, size);
      }
      ctx.globalAlpha = 1;

      // Keep animating while dots are still rising or fading; a resting cursor
      // with settled dots stops the loop until the next move or scroll.
      frameId = moving ? requestAnimationFrame(frame) : 0;
    };

    const wake = () => {
      if (!frameId) {
        last = 0;
        frameId = requestAnimationFrame(frame);
      }
    };

    const onMove = (e: PointerEvent) => {
      if (e.pointerType === "touch") return;
      if (!canvas.width) fit(); // only allocate the canvas once a mouse shows up
      pointer = { x: e.clientX, y: e.clientY };
      wake();
    };
    const onLeave = () => {
      pointer = null;
      if (heights.size) wake();
    };
    const onScroll = () => {
      if (pointer || heights.size) wake();
    };
    const onResize = () => {
      if (!canvas.width) return;
      fit();
      wake();
    };

    const root = document.documentElement;
    window.addEventListener("pointermove", onMove, { passive: true });
    root.addEventListener("pointerleave", onLeave);
    window.addEventListener("blur", onLeave);
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("pointermove", onMove);
      root.removeEventListener("pointerleave", onLeave);
      window.removeEventListener("blur", onLeave);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onResize);
      cancelAnimationFrame(frameId);
    };
  }, []);

  // -z-10 inside the page's isolated root: above its background color, below
  // every section, so lit dots show through the page but never over content.
  return (
    <canvas
      ref={canvasRef}
      width={0}
      height={0}
      aria-hidden="true"
      className="fixed left-0 top-0 -z-10 pointer-events-none"
    />
  );
}

export default DotGridGlow;
